import { Music, Pause, Play } from "lucide-react";
import { useRef, useState } from "react";

import { cn } from "~/shared/lib/utils";

export interface AudioPlayTileProps {
    src: string;
    /** Подписи кнопки — их даёт caller: локали живут в его namespace. */
    label: { play: string; pause: string };
    /** Размер круглой кнопки: плитка пикера крупная, плитка промпт-бара мелкая. */
    size?: "sm" | "md";
    className?: string;
}

/**
 * Плитка звука: иконка в углу + кнопка Play/Pause по центру.
 *
 * У аудио нет кадра, поэтому без такой плитки прикреплённый звук выглядит немой
 * иконкой и прослушать его нельзя нигде, кроме как скачав файл. Один примитив на
 * все места, где звук показывается квадратом — пикер вложений и очередь
 * промпт-бара.
 *
 * `<audio preload="none">` — файл не качается, пока не нажали Play.
 * `stopPropagation` нужен, чтобы клик не проваливался в overlay-кнопку плитки
 * (она выше по z-index) и не начинал перетаскивание в сортируемой очереди.
 */
export function AudioPlayTile({ src, label, size = "md", className }: AudioPlayTileProps) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [playing, setPlaying] = useState(false);

    const toggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) void audio.play();
        else audio.pause();
    };

    const isSmall = size === "sm";

    return (
        <div
            className={cn(
                "relative flex h-full w-full items-center justify-center bg-zinc-900 text-violet-300",
                className,
            )}
        >
            <Music
                className={cn(
                    "absolute opacity-70",
                    isSmall ? "top-1 left-1 h-3 w-3" : "top-2 left-2 h-4 w-4",
                )}
            />
            <button
                type="button"
                onClick={toggle}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label={playing ? label.pause : label.play}
                className={cn(
                    "z-20 flex items-center justify-center rounded-full bg-violet-500/30 backdrop-blur-md transition-colors",
                    "hover:bg-violet-500/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300",
                    isSmall ? "h-7 w-7" : "h-12 w-12",
                )}
            >
                {playing ? (
                    <Pause className={isSmall ? "h-3.5 w-3.5" : "h-5 w-5"} />
                ) : (
                    <Play className={cn("ml-0.5", isSmall ? "h-3.5 w-3.5" : "h-5 w-5")} />
                )}
            </button>
            <audio
                ref={audioRef}
                src={src}
                preload="none"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
            />
        </div>
    );
}
